import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faBars,
  faChevronDown,
  faRefresh,
  faBell,
  faUser,
  faSearch,
} from '@fortawesome/free-solid-svg-icons';
import {
  APIProvider,
  Map,
  Marker,
  InfoWindow,
  useMap,
} from '@vis.gl/react-google-maps';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import DriverModal from '../components/DriverModal';

const defaultCenter = {
  lat: 41.015137, // Turkey coordinates as default
  lng: 28.97953,
};

// Component that uses useMap hook to control map focus
const MapController = ({ focusTarget, onMapReady }) => {
  const map = useMap();

  useEffect(() => {
    if (map && onMapReady) {
      onMapReady(map);
    }
  }, [map, onMapReady]);

  useEffect(() => {
    if (!map || !focusTarget) return;

    const position = {
      lat: focusTarget.latitude,
      lng: focusTarget.longitude,
    };

    try {
      // Pan to the driver's location and zoom in
      map.panTo(position);
      map.setZoom(15);
    } catch (error) {
      console.error('Error focusing on driver:', error);
    }
  }, [map, focusTarget]);

  return null; // This component doesn't render anything
};

// Format date helper function
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('tr-TR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

// Add this function near the top with other helper functions
const fetchDriverInfo = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .eq('status', 'approved')
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error fetching driver info:', error.message);
    return null;
  }
};

// Update the helper function to handle JSONB accepted_driver field
const checkDriverAssignedLoad = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('loads')
      .select('*')
      .contains('accepted_driver', { id: userId })
      .eq('status', 'assigned')
      .single();

    if (error) throw error;
    return !!data; // Returns true if there's an assigned load, false otherwise
  } catch (error) {
    console.error('Error checking driver assigned load:', error.message);
    return false;
  }
};

// Function to filter locations from approved drivers only (excluding company users)
const filterApprovedDriverLocations = async (locations) => {
  try {
    // Get all user IDs from locations
    const userIds = locations.map((location) => location.user_id);

    if (userIds.length === 0) return [];

    // Fetch profiles for all users to check user_type and status
    const { data, error } = await supabase
      .from('profiles')
      .select('id, user_type, status')
      .in('id', userIds);

    if (error) throw error;

    // Create a map of user_id to profile data
    const userProfileMap = {};
    data.forEach((profile) => {
      userProfileMap[profile.id] = profile;
    });

    // Filter locations to only include approved drivers (excluding company users)
    return locations.filter((location) => {
      const profile = userProfileMap[location.user_id];
      return (
        profile &&
        profile.user_type !== 'company' &&
        profile.status === 'approved'
      );
    });
  } catch (error) {
    console.error('Error filtering approved driver locations:', error.message);
    return locations; // Return original locations if there was an error
  }
};

function Locations() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [driverInfo, setDriverInfo] = useState(null);
  // Set default startDate to yesterday
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayFormatted = yesterday.toISOString().split('T')[0];

  // Set default endDate to today
  const today = new Date();
  const todayFormatted = today.toISOString().split('T')[0];

  const [startDate, setStartDate] = useState(yesterdayFormatted);
  const [endDate, setEndDate] = useState(todayFormatted);
  const [markerStates, setMarkerStates] = useState({});
  const [mapError, setMapError] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [isSendingNotification, setIsSendingNotification] = useState(false);
  const [isFilterLoading, setIsFilterLoading] = useState(false);

  // States for DriverModal
  const [isDriverModalOpen, setIsDriverModalOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);

  // States for driver list
  const [driversWithLocations, setDriversWithLocations] = useState([]);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // States for driver tabs
  const [activeTab, setActiveTab] = useState('all');

  // State for map focus control
  const [mapFocusTarget, setMapFocusTarget] = useState(null);
  const [mapInstance, setMapInstance] = useState(null);

  // State for storing driver info for each location (for map labels)
  const [locationDriverInfo, setLocationDriverInfo] = useState({});

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  // Function to send location notification to all drivers
  const sendLocationNotification = async () => {
    try {
      setIsSendingNotification(true);

      // Fetch all drivers with push tokens
      const { data: drivers, error: driversError } = await supabase
        .from('profiles')
        .select('id, push_token')
        .eq('user_type', 'driver')
        .not('push_token', 'is', null);

      if (driversError) throw driversError;

      if (!drivers || drivers.length === 0) {
        alert('Bildirim gönderilebilecek sürücü bulunamadı');
        return;
      }

      // Prepare notification message
      const notificationMessage =
        'Taşıma sürecinin sağlıklı ilerleyebilmesi ve yeni taşıma fırsatları için konum bilginizi paylaşmanız gerekiyor. Konumunuzu iletmek için bildirime dokunun.';

      // Collect all valid push tokens
      const validTokens = drivers
        .filter((driver) => driver.push_token)
        .map((driver) => driver.push_token);

      // Create notification messages
      const createMessage = (token) => ({
        to: token,
        sound: 'default',
        title: 'Konum Bildirimi',
        body: notificationMessage,
        data: { type: 'location_request' },
      });

      // Send notifications using Supabase function to avoid CORS issues
      // This approach also prevents duplicate notifications on iOS
      const sendNotifications = async () => {
        if (validTokens.length === 0) return 0;

        try {
          // Use the Supabase Edge Function to send notifications
          // This avoids CORS issues as it's a server-to-server request
          const { error } = await supabase.functions.invoke(
            'send-notification',
            {
              body: {
                message: validTokens.map((token) => createMessage(token)),
              },
            }
          );

          if (error) throw error;
          return validTokens.length;
        } catch (err) {
          console.error(
            'Error sending notifications via Supabase function:',
            err
          );

          // Fallback to direct API call with no-cors mode if Supabase function fails
          // This is not ideal but ensures notifications are sent even if the function fails
          const notificationPromises = validTokens.map((token) => {
            return fetch('https://exp.host/--/api/v2/push/send', {
              method: 'POST',
              headers: {
                Accept: 'application/json',
                'Accept-encoding': 'gzip, deflate',
                'Content-Type': 'application/json',
              },
              mode: 'no-cors', // Required for CORS but may cause duplicates on iOS
              body: JSON.stringify(createMessage(token)),
            });
          });

          await Promise.all(notificationPromises);
          return validTokens.length;
        }
      };

      // Send notifications to all devices
      const sentCount = await sendNotifications();

      alert(`${sentCount} sürücüye konum bildirimi gönderildi`);
    } catch (error) {
      console.error('Error sending notifications:', error);
      alert('Bildirim gönderilirken bir hata oluştu');
    } finally {
      setIsSendingNotification(false);
    }
  };

  // Add error handling for map
  const handleMapError = (error) => {
    console.error('Google Maps Error:', error);
    setMapError('Failed to load map. Please try again later.');
  };

  // Add this effect to handle async marker states and fetch driver info for labels
  useEffect(() => {
    const updateMarkerStatesAndDriverInfo = async () => {
      const states = {};
      const driverInfoMap = {};

      for (const location of filteredLocations) {
        if (location?.latitude && location?.longitude) {
          try {
            // Check load status
            const hasAssignedLoad = await checkDriverAssignedLoad(
              location.user_id
            );
            states[location.location_id] = hasAssignedLoad;

            // Fetch driver info for labels
            const driverInfo = await fetchDriverInfo(location.user_id);
            driverInfoMap[location.location_id] = driverInfo;
          } catch (error) {
            console.error(
              'Error checking driver load or fetching info:',
              error
            );
            states[location.location_id] = false;
            driverInfoMap[location.location_id] = null;
          }
        }
      }
      setMarkerStates(states);
      setLocationDriverInfo(driverInfoMap);
    };

    updateMarkerStatesAndDriverInfo();
  }, [filteredLocations]);

  // Add this effect to filter approved driver locations after locations are fetched
  useEffect(() => {
    const filterLocations = async () => {
      if (locations.length > 0) {
        const filtered = await filterApprovedDriverLocations(locations);
        setFilteredLocations(filtered);
      } else {
        setFilteredLocations([]);
      }
    };

    filterLocations();
  }, [locations]);

  const handleMarkerClick = async (location) => {
    try {
      const info = await fetchDriverInfo(location.user_id);
      setDriverInfo(info);
      setSelectedMarker(location);
    } catch (error) {
      console.error('Error fetching driver info:', error);
      setDriverInfo(null);
      setSelectedMarker(location);
    }
  };

  const handleInfoWindowClose = () => {
    setSelectedMarker(null);
    setDriverInfo(null);
  };

  // Function to open the driver modal with the selected driver
  const openDriverModal = () => {
    if (driverInfo) {
      setSelectedDriver(driverInfo);
      setIsDriverModalOpen(true);
    }
  };

  // Function to handle closing the driver modal
  const handleDriverModalClose = () => {
    setIsDriverModalOpen(false);
  };

  // Function to handle saving changes in the driver modal
  const handleDriverModalSave = () => {
    // Refresh driver info after saving changes
    if (selectedMarker && selectedMarker.user_id) {
      fetchDriverInfo(selectedMarker.user_id).then((info) => {
        setDriverInfo(info);
      });
    }
  };

  // Function to fetch drivers with their location information
  const fetchDriversWithLocations = async () => {
    try {
      setLoadingDrivers(true);

      // Fetch all drivers
      const { data: drivers, error: driversError } = await supabase
        .from('profiles')
        .select('*')
        .in('user_type', ['driver', 'company_driver'])
        .eq('status', 'approved')
        .order('created_at', { ascending: false });

      if (driversError) throw driversError;

      if (drivers.length === 0) {
        setDriversWithLocations([]);
        return;
      }

      // Build query for locations with date filters
      let locationsQuery = supabase
        .from('last_locations')
        .select('*')
        .order('created_at', { ascending: false });

      // Apply date filters if they exist
      if (startDate) {
        locationsQuery = locationsQuery.gte(
          'created_at',
          `${startDate}T00:00:00`
        );
      }
      if (endDate) {
        locationsQuery = locationsQuery.lte(
          'created_at',
          `${endDate}T23:59:59`
        );
      }
      const { data: locationsData, error: locationsError } =
        await locationsQuery;

      if (locationsError) throw locationsError;
 
      // Create a map of the latest location for each driver
      const latestLocations = {};
      locationsData.forEach((location) => {
        if (
          !latestLocations[location.user_id] ||
          new Date(location.created_at) >
            new Date(latestLocations[location.user_id].created_at)
        ) {
          latestLocations[location.user_id] = location;
        }
      });

      // Check load status for each driver
      const driversWithLocationData = [];
      for (const driver of drivers) {
        const location = latestLocations[driver.id];
        if (location) {
          try {
            const hasAssignedLoad = await checkDriverAssignedLoad(driver.id);
            driversWithLocationData.push({
              ...driver,
              location,
              hasAssignedLoad,
            });
          } catch (error) {
            console.error('Error checking driver load:', error);
            driversWithLocationData.push({
              ...driver,
              location,
              hasAssignedLoad: false,
            });
          }
        }
      }

      setDriversWithLocations(driversWithLocationData);
    } catch (error) {
      console.error('Error fetching drivers with locations:', error);
    } finally {
      setLoadingDrivers(false);
    }
  };

  // Function to focus map on a specific driver's location
  const focusOnDriver = (driver) => {
    if (!driver.location) {
      return;
    }

    // Set the focus target which will trigger the MapController
    setMapFocusTarget(driver.location);

    // Also set the selected marker to show info window
    setSelectedMarker(driver.location);
    fetchDriverInfo(driver.id).then((info) => {
      setDriverInfo(info);
    });
  };

  const isLocationOld = (timestamp) => {
    if (!timestamp) return true;

    const locationTime = new Date(timestamp);
    const currentTime = new Date();
    const sixHoursAgo = new Date(currentTime.getTime() - 6 * 60 * 60 * 1000);

    return locationTime < sixHoursAgo;
  };

  // Function to get count for each tab
  const getTabCounts = () => {
    const counts = {
      all: 0,
      moving: 0,
      stationary: 0,
      no_data: 0,
    };

    driversWithLocations.forEach((driver) => {
      // Apply company filter first
      if (selectedCompany && driver.company !== selectedCompany) {
        return;
      }

      // Apply search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const fullName =
          `${driver.first_name || ''} ${driver.last_name || ''}`.toLowerCase();
        const phoneNumber = (driver.phone_number || '').toLowerCase();
        const company = (driver.company || '').toLowerCase();
        const towPlate = (driver.tow_plate || '').toLowerCase();
        const trailerPlate = (driver.trailer_plate || '').toLowerCase();

        const matchesSearch =
          fullName.includes(searchLower) ||
          phoneNumber.includes(searchLower) ||
          company.includes(searchLower) ||
          towPlate.includes(searchLower) ||
          trailerPlate.includes(searchLower);

        if (!matchesSearch) return;
      }

      const speed = driver.location?.speed;
      const locationTime = driver.location?.created_at
      counts.all++;

      if(isLocationOld(locationTime)){
        counts.no_data++;
      } else if (speed !== null && speed !== undefined && speed > 0 && !isLocationOld(locationTime)) {
        counts.moving++;
      } else if (speed === null || speed === undefined){
        counts.stationary++;
      }
    });

    return counts;
  };

  const tabCounts = getTabCounts();

  // Function to filter drivers based on search term, active tab, and company filter
  const filteredDriversWithLocations = driversWithLocations.filter((driver) => {
    // First apply company filter
    if (selectedCompany && driver.company !== selectedCompany) {
      return false;
    }

    // Then apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const fullName =
        `${driver.first_name || ''} ${driver.last_name || ''}`.toLowerCase();
      const phoneNumber = (driver.phone_number || '').toLowerCase();
      const company = (driver.company || '').toLowerCase();
      const towPlate = (driver.tow_plate || '').toLowerCase();
      const trailerPlate = (driver.trailer_plate || '').toLowerCase();

      const matchesSearch =
        fullName.includes(searchLower) ||
        phoneNumber.includes(searchLower) ||
        company.includes(searchLower) ||
        towPlate.includes(searchLower) ||
        trailerPlate.includes(searchLower);

      if (!matchesSearch) return false;
    }

    // Finally apply tab filter based on speed
    const speed = driver.location?.speed;
    const location = driver.location?.created_at;

    switch (activeTab) {
      case 'all':
        return true;
      case 'no_data':
        return isLocationOld(location);
      case 'moving':
        return speed !== null && speed !== undefined && speed > 0 && !isLocationOld(location);
      case 'stationary':
        return speed === null || speed === undefined;
      default:
        return true;
    }
  });

  // Add this function to fit bounds
  const fitBoundsToMarkers = useCallback(() => {
    if (!mapInstance || !filteredLocations.length) return;

    const bounds = new window.google.maps.LatLngBounds();
    filteredLocations.forEach((location) => {
      if (location?.latitude && location?.longitude) {
        bounds.extend({
          lat: location.latitude,
          lng: location.longitude,
        });
      }
    });

    mapInstance.fitBounds(bounds, {
      padding: { top: 50, bottom: 50, left: 50, right: 50 },
    });
  }, [filteredLocations, mapInstance]);

  // Add this effect to fit bounds when markers or map changes
  useEffect(() => {
    if (filteredLocations.length > 0 && mapInstance && !loading) {
      fitBoundsToMarkers();
    }
  }, [filteredLocations, loading, fitBoundsToMarkers, mapInstance]);

  // Function to fetch companies
  const fetchCompanies = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, company_name')
        .eq('user_type', 'company');

      if (error) throw error;
      setCompanies(data || []);
    } catch (error) {
      console.error('Error fetching companies:', error.message);
    }
  };

  // Fetch locations and companies on mount
  useEffect(() => {
    fetchLocations();
    fetchCompanies();
    fetchDriversWithLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Add useEffect to refetch when dates or company filter changes
  useEffect(() => {
    fetchLocations();
    fetchDriversWithLocations(); // Also refresh driver list when filters change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, selectedCompany]);

  const fetchLocations = async () => {
    try {
      setLoading(true);

      let query = supabase
        .from('last_locations')
        .select('*, created_at')
        .order('created_at', { ascending: false });

      // Add date filters if they exist
      if (startDate) {
        query = query.gte('created_at', startDate + 'T00:00:00');
      }
      if (endDate) {
        query = query.lte('created_at', endDate + 'T23:59:59');
      }

      const { data, error } = await query;

      if (error) throw error;

      // Filter to keep only the most recent location for each user
      const latestLocations = data.reduce((acc, current) => {
        const existingLocation = acc.find(
          (item) => item.user_id === current.user_id
        );

        if (
          !existingLocation ||
          new Date(current.created_at) > new Date(existingLocation.created_at)
        ) {
          // Remove the existing location if there is one
          const filteredAcc = acc.filter(
            (item) => item.user_id !== current.user_id
          );
          // Add the current location
          return [...filteredAcc, current];
        }

        return acc;
      }, []);

      // If company filter is selected, we'll need to fetch profiles to filter by company
      if (selectedCompany) {
        // Get all user IDs from locations
        const userIds = latestLocations.map((location) => location.user_id);

        if (userIds.length > 0) {
          // Fetch profiles for these users
          const { data: profilesData, error: profilesError } = await supabase
            .from('profiles')
            .select('id, company')
            .in('id', userIds);

          if (profilesError) throw profilesError;

          // Filter locations to only include users with the selected company
          const filteredByCompany = latestLocations.filter((location) => {
            const userProfile = profilesData.find(
              (profile) => profile.id === location.user_id
            );
            return userProfile && userProfile.company === selectedCompany;
          });

          setLocations(filteredByCompany || []);
        } else {
          setLocations([]);
        }
      } else {
        setLocations(latestLocations || []);
      }
    } catch (error) {
      console.error('Error fetching locations:', error.message);
    } finally {
      setLoading(false);
      setIsFilterLoading(false);
    }
  };


  // saat 8.00 ve 17.00'da lokasyonların güncellenmesi
  useEffect(() => {
    const KEY = "__lastRunMinute";
    const RUN_TIMES = [{ h: 8, m: 0 }, { h: 17, m: 0 }];
    let running = false;

    const id = setInterval(() => {
      const now = new Date();
      const h = now.getHours(), m = now.getMinutes();
      const match = RUN_TIMES.some(t => t.h === h && t.m === m);
      if (!match) return;

      const cur = String(h * 60 + m);
      if (sessionStorage.getItem(KEY) === cur || running) return;

      running = true;
      try {
        fetchDriversWithLocations();
        fetchLocations();
      } finally {
        sessionStorage.setItem(KEY, cur);
        running = false;
      }
    }, 30 * 1000);

    return () => clearInterval(id);
  }, []);


  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Add custom CSS for marker labels */}
      <style>{`
        .marker-label-green {
          background-color: #4CAF50 !important;
          padding: 4px 8px !important;
          border-radius: 4px !important;
          border: 1px solid rgba(255, 255, 255, 0.3) !important;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3) !important;
          white-space: pre-line !important;
          text-align: center !important;
          line-height: 1.2 !important;
          font-family: 'Arial', sans-serif !important;
          transform: translateY(-100%) !important;
          margin-top: -5px !important;
          color: #FFFFFF !important;
          font-weight: bold !important;
          font-size: 11px !important;
        }
        .marker-label-red {
          background-color: #FF0000 !important;
          padding: 4px 8px !important;
          border-radius: 4px !important;
          border: 1px solid rgba(255, 255, 255, 0.3) !important;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3) !important;
          white-space: pre-line !important;
          text-align: center !important;
          line-height: 1.2 !important;
          font-family: 'Arial', sans-serif !important;
          transform: translateY(-100%) !important;
          margin-top: -5px !important;
          color: #FFFFFF !important;
          font-weight: bold !important;
          font-size: 11px !important;
        }
      `}</style>

      {/* Driver Modal */}
      {isDriverModalOpen && (
        <DriverModal
          isOpen={isDriverModalOpen}
          onClose={handleDriverModalClose}
          driver={selectedDriver}
          onSave={handleDriverModalSave}
        />
      )}

      {/* Top Navigation */}
      <header className="bg-white shadow">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button className="text-gray-500 lg:hidden">
                <FontAwesomeIcon icon={faBars} className="text-xl" />
              </button>
            </div>

            <div className="relative">
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 focus:outline-none"
              >
                <span>{user?.email}</span>
                <FontAwesomeIcon icon={faChevronDown} className="text-xs" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100">
        <div className="container mx-auto px-6 py-8">
          <div className="flex items-center justify-between pb-6">
            <div>
              <h1 className="text-2xl font-medium text-gray-800">
                Lokasyonlar
              </h1>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Başlangıç Tarihi
                </label>
                <div className="relative">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => {
                      setIsFilterLoading(true);
                      setStartDate(e.target.value);
                    }}
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isFilterLoading}
                  />
                  {isFilterLoading && startDate && (
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                      <FontAwesomeIcon
                        icon={faSpinner}
                        spin
                        className="text-[#4263EB] text-lg"
                      />
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bitiş Tarihi
                </label>
                <div className="relative">
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => {
                      setIsFilterLoading(true);
                      setEndDate(e.target.value);
                    }}
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isFilterLoading}
                  />
                  {isFilterLoading && endDate && (
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                      <FontAwesomeIcon
                        icon={faSpinner}
                        spin
                        className="text-[#4263EB] text-lg"
                      />
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Şirket
                </label>
                <div className="relative">
                  <select
                    value={selectedCompany}
                    onChange={(e) => {
                      setIsFilterLoading(true);
                      setSelectedCompany(e.target.value);
                    }}
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isFilterLoading}
                  >
                    <option value="">Tüm Şirketler</option>
                    {companies.map((company) => (
                      <option key={company.id} value={company.company_name}>
                        {company.company_name}
                      </option>
                    ))}
                  </select>
                  {isFilterLoading && (
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                      <FontAwesomeIcon
                        icon={faSpinner}
                        spin
                        className="text-[#4263EB] text-lg"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <div className="flex justify-end space-x-2">
              <button
                onClick={sendLocationNotification}
                disabled={loading || isSendingNotification || isFilterLoading}
                className="bg-[#4CAF50] text-white px-4 py-2 rounded-md hover:bg-[#45a049] flex items-center"
              >
                <FontAwesomeIcon
                  icon={isSendingNotification ? faSpinner : faBell}
                  spin={isSendingNotification}
                  className="mr-2"
                />
                Konum Bildirimi Gönder
              </button>
              <button
                onClick={() => {
                  setIsFilterLoading(true);
                  fetchLocations();
                  fetchDriversWithLocations();
                }}
                disabled={loading || isFilterLoading}
                className="bg-[#4263EB] text-white px-4 py-2 rounded-md hover:bg-[#3b5bd9] flex items-center"
              >
                <FontAwesomeIcon
                  icon={isFilterLoading ? faSpinner : faRefresh}
                  spin={isFilterLoading}
                  className="mr-2"
                />
                Lokasyonları Yenile
              </button>
            </div>
          </div>

          {/* Main Content: Map and Driver List */}
          <div className="flex gap-6">
            {/* Map Section */}
            <div className="flex-1 bg-white rounded-lg shadow overflow-hidden p-4">
              {loading ? (
                <div className="flex justify-center items-center h-[600px]">
                  <FontAwesomeIcon
                    icon={faSpinner}
                    spin
                    className="text-[#4263EB] text-3xl"
                  />
                  <span className="ml-2 text-gray-600">Yükleniyor...</span>
                </div>
              ) : mapError ? (
                <div className="flex justify-center items-center h-[600px]">
                  <p className="text-red-500">{mapError}</p>
                </div>
              ) : (
                <>
                  <APIProvider
                    apiKey={'AIzaSyDB8KdSyduBU2vadQbJhojnwu5gNsj2f-o'}
                    onError={handleMapError}
                    libraries={['places']}
                  >
                    <div className="w-full h-[600px]">
                      <Map
                        defaultCenter={defaultCenter}
                        defaultZoom={6}
                        gestureHandling="greedy"
                        onLoad={() => {
                          if (filteredLocations.length > 0) {
                            fitBoundsToMarkers();
                          }
                        }}
                        options={{
                          zoomControl: true,
                          mapTypeControl: true,
                          scaleControl: true,
                          streetViewControl: true,
                          rotateControl: true,
                          fullscreenControl: true,
                        }}
                      >
                        {/* Map Controller for focusing on drivers */}
                        <MapController
                          focusTarget={mapFocusTarget}
                          onMapReady={setMapInstance}
                        />
                        {filteredLocations.map((location) => {
                          if (!location?.latitude || !location?.longitude)
                            return null;

                          // Get driver info for this location
                          const driverInfo = locationDriverInfo[location.location_id];

                          // Create label text with driver name and plates
                          let labelText = '';
                          if (driverInfo) {
                            const name =
                              `${driverInfo.first_name || ''} ${driverInfo.last_name || ''}`.trim();
                            const towPlate = driverInfo.tow_plate || '';
                            const trailerPlate = driverInfo.trailer_plate || '';

                            if (name) labelText += name;
                            if (towPlate)
                              labelText += (labelText ? '\n' : '') + towPlate;
                            if (trailerPlate)
                              labelText +=
                                (labelText ? '\n' : '') + trailerPlate;
                          }

                          return (
                            <Marker
                              key={location.location_id}
                              position={{
                                lat: location.latitude,
                                lng: location.longitude,
                              }}
                              onClick={() => handleMarkerClick(location)}
                              options={{
                                icon: {
                                  url:
                                    'data:image/svg+xml,' +
                                    encodeURIComponent(
                                      '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="10" cy="10" r="8" fill="' +
                                        (markerStates[location.location_id]
                                          ? '#4CAF50'
                                          : '#FF0000') +
                                        '" stroke="white" stroke-width="2"/></svg>'
                                    ),
                                },
                                label: labelText
                                  ? {
                                      text: labelText,
                                      color: '#FFFFFF',
                                      fontSize: '11px',
                                      fontWeight: 'bold',
                                      className: markerStates[location.location_id]
                                        ? 'marker-label-green'
                                        : 'marker-label-red',
                                    }
                                  : undefined,
                              }}
                            />
                          );
                        })}

                        {selectedMarker && (
                          <InfoWindow
                            position={{
                              lat: selectedMarker.latitude,
                              lng: selectedMarker.longitude,
                            }}
                            onCloseClick={handleInfoWindowClose}
                          >
                            <div style={{ padding: '8px' }}>
                              {selectedMarker.address && (
                                <p
                                  style={{
                                    margin: '0 0 8px 0',
                                    fontSize: '14px',
                                  }}
                                >
                                  {selectedMarker.address}
                                </p>
                              )}
                              {driverInfo ? (
                                <div
                                  style={{
                                    margin: '8px 0',
                                    padding: '8px',
                                    background: '#f5f5f5',
                                    borderRadius: '4px',
                                  }}
                                >
                                  <h4
                                    style={{
                                      margin: '0 0 4px 0',
                                      fontSize: '14px',
                                    }}
                                  >
                                    Sürücü Bilgileri
                                  </h4>
                                  <p
                                    style={{
                                      margin: '0 0 4px 0',
                                      fontSize: '13px',
                                    }}
                                  >
                                    Adı: {driverInfo.first_name || 'N/A'}{' '}
                                    {driverInfo.last_name || 'N/A'}
                                  </p>
                                  <p
                                    style={{
                                      margin: '0 0 4px 0',
                                      fontSize: '13px',
                                    }}
                                  >
                                    Telefon: {driverInfo.phone_number || 'N/A'}
                                  </p>
                                  {driverInfo.company && (
                                    <p
                                      style={{
                                        margin: '0 0 4px 0',
                                        fontSize: '13px',
                                      }}
                                    >
                                      Şirket: {driverInfo.company || 'N/A'}
                                    </p>
                                  )}
                                  {driverInfo.tow_plate && (
                                    <p
                                      style={{
                                        margin: '0 0 4px 0',
                                        fontSize: '13px',
                                      }}
                                    >
                                      Çekici Plaka: {driverInfo.tow_plate}
                                    </p>
                                  )}
                                  {driverInfo.trailer_plate && (
                                    <p
                                      style={{
                                        margin: '0 0 4px 0',
                                        fontSize: '13px',
                                      }}
                                    >
                                      Dorse Plaka: {driverInfo.trailer_plate}
                                    </p>
                                  )}
                                  <button
                                    onClick={openDriverModal}
                                    style={{
                                      marginTop: '8px',
                                      padding: '4px 8px',
                                      backgroundColor: '#4263EB',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      fontSize: '12px',
                                      cursor: 'pointer',
                                    }}
                                  >
                                    <FontAwesomeIcon
                                      icon={faUser}
                                      className="mr-1"
                                    />
                                    Detaylar
                                  </button>
                                </div>
                              ) : (
                                <p
                                  style={{
                                    margin: '8px 0',
                                    color: '#666',
                                    fontSize: '13px',
                                  }}
                                >
                                  Sürücü bilgileri mevcut değil
                                </p>
                              )}
                              <p
                                style={{
                                  margin: 0,
                                  fontSize: '12px',
                                  color: '#666',
                                }}
                              >
                                Kaydedildi:{' '}
                                {formatDate(selectedMarker.created_at)}
                              </p>
                            </div>
                          </InfoWindow>
                        )}
                      </Map>
                    </div>
                  </APIProvider>
                  <div className="flex items-center mt-2 text-sm">
                    <div className="flex items-center mr-4">
                      <div className="w-4 h-4 rounded-full bg-green-500 mr-2"></div>
                      <span>İş atanmış Sürücüler</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-4 h-4 rounded-full bg-red-500 mr-2"></div>
                      <span>İş atanmamış Sürücüler</span>
                    </div>
                    <div className="ml-auto text-sm text-gray-600">
                      {filteredLocations.length} konum gösteriliyor
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Driver List Section */}
            <div className="w-96 bg-white rounded-lg shadow overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b">
                <h3 className="text-lg font-medium text-gray-900">
                  Sürücü Listesi
                </h3>
                <p className="text-sm text-gray-500">
                  {filteredDriversWithLocations.length} sürücü
                </p>
              </div>

              {/* Tabs */}
              <div className="bg-white border-b">
                <div className="flex">
                  <button
                    onClick={() => setActiveTab('all')}
                    className={`flex-1 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
                      activeTab === 'all'
                        ? 'border-blue-500 text-blue-600 bg-blue-50'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <div className="text-center">
                      <div>Tümü</div>
                      <div className="text-xs font-normal">
                        ({tabCounts.all})
                      </div>
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveTab('moving')}
                    className={`flex-1 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
                      activeTab === 'moving'
                        ? 'border-green-500 text-green-600 bg-green-50'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <div className="text-center">
                      <div>Hareketli</div>
                      <div className="text-xs font-normal">
                        ({tabCounts.moving})
                      </div>
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveTab('stationary')}
                    className={`flex-1 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
                      activeTab === 'stationary'
                        ? 'border-orange-500 text-orange-600 bg-orange-50'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <div className="text-center">
                      <div>Duran</div>
                      <div className="text-xs font-normal">
                        ({tabCounts.stationary})
                      </div>
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveTab('no_data')}
                    className={`flex-1 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
                      activeTab === 'no_data'
                        ? 'border-red-500 text-red-600 bg-red-50'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <div className="text-center">
                      <div>Veri Alınamayan</div>
                      <div className="text-xs font-normal">
                        ({tabCounts.no_data})
                      </div>
                    </div>
                  </button>
                </div>
              </div>
              {/* Search Input */}
              <div className="p-4 border-b">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Sürücü ara (isim, telefon, plaka...)"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-4 py-2 pl-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  />
                </div>
              </div>
              <div className="h-[540px] overflow-y-auto">
                {loadingDrivers ? (
                  <div className="flex justify-center items-center h-full">
                    <FontAwesomeIcon
                      icon={faSpinner}
                      spin
                      className="text-[#4263EB] text-xl"
                    />
                    <span className="ml-2 text-gray-600">
                      Sürücüler yükleniyor...
                    </span>
                  </div>
                ) : filteredDriversWithLocations.length === 0 ? (
                  <div className="flex justify-center items-center h-full">
                    <p className="text-gray-500">
                      {searchTerm
                        ? 'Arama kriterlerine uygun sürücü bulunamadı'
                        : 'Konum bilgisi olan sürücü bulunamadı'}
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {filteredDriversWithLocations.map((driver) => (
                      <div
                        key={driver.id}
                        className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => focusOnDriver(driver)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center">
                              <div
                                className={`w-3 h-3 rounded-full mr-2 ${
                                  driver.hasAssignedLoad
                                    ? 'bg-green-500'
                                    : 'bg-red-500'
                                }`}
                              ></div>
                              <h4 className="text-sm font-medium text-gray-900">
                                {driver.first_name} {driver.last_name}
                              </h4>
                              {/* Speed indicator */}
                              <div className="ml-2">
                                {driver.location?.speed !== null &&
                                driver.location?.speed !== undefined ? (
                                  <span
                                    className={`px-1.5 py-0.5 text-xs rounded-full font-medium ${
                                      driver.location.speed > 0
                                        ? 'bg-green-100 text-green-700'
                                        : 'bg-orange-100 text-orange-700'
                                    }`}
                                  >
                                    {driver.location.speed} km/h
                                  </span>
                                ) : (
                                  <span className="px-1.5 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600 font-medium">
                                    Veri Yok
                                  </span>
                                )}
                              </div>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              {driver.phone_number || 'N/A'}
                            </p>
                            {driver.company && (
                              <p className="text-xs text-gray-500">
                                {driver.company}
                              </p>
                            )}
                            {driver.tow_plate && (
                              <p className="text-xs text-gray-500">
                                Çekici: {driver.tow_plate}
                              </p>
                            )}
                            {driver.trailer_plate && (
                              <p className="text-xs text-gray-500">
                                Dorse: {driver.trailer_plate}
                              </p>
                            )}
                            <p className="text-xs text-gray-400 mt-1">
                              Son konum:{' '}
                              {formatDate(driver.location.created_at)}
                            </p>
                            {driver.location.address && (
                              <p className="text-xs text-gray-400 truncate">
                                {driver.location.address}
                              </p>
                            )}
                          </div>
                          <div className="ml-2">
                            <span
                              className={`px-2 py-1 text-xs rounded-full ${
                                driver.hasAssignedLoad
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {driver.hasAssignedLoad ? 'Yük Var' : 'Boş'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default Locations;
